
import { useState, useEffect } from 'react';

interface Mattress {
  id: string;
  name: string;
  brand: string;
  collection: string;
  feel: string;
  size: string;
  quantity: number;
}

// Create a singleton state to share across components
let globalList: Mattress[] = [];
let globalListeners: (() => void)[] = [];

const notifyListeners = () => {
  globalListeners.forEach(listener => listener());
};

export const useQuoteList = () => {
  const [list, setList] = useState<Mattress[]>(globalList);
  const [isOpen, setIsOpen] = useState(false);

  // Subscribe to global state changes
  useEffect(() => {
    const listener = () => {
      setList([...globalList]);
    };
    globalListeners.push(listener);

    // Load from localStorage on mount
    const saved = localStorage.getItem('mattress-quote-list');
    if (saved && globalList.length === 0) {
      try {
        globalList = JSON.parse(saved);
        setList([...globalList]);
        notifyListeners();
      } catch (error) {
        console.error('Error loading quote list:', error);
      }
    }

    return () => {
      globalListeners = globalListeners.filter(l => l !== listener);
    };
  }, []);

  // Save to localStorage whenever list changes
  useEffect(() => {
    localStorage.setItem('mattress-quote-list', JSON.stringify(list));
  }, [list]);

  const addToList = (mattress: Omit<Mattress, 'quantity'>) => {
    // Check if already in list
    const existingIndex = globalList.findIndex(item => item.id === mattress.id);
    if (existingIndex !== -1) {
      // If already exists, increase quantity
      globalList[existingIndex].quantity += 1;
    } else {
      // Add new item with quantity 1
      globalList = [...globalList, { ...mattress, quantity: 1 }];
    }
    localStorage.setItem('mattress-quote-list', JSON.stringify(globalList));
    notifyListeners();
  };

  const removeFromList = (mattressId: string) => {
    globalList = globalList.filter(item => item.id !== mattressId);
    localStorage.setItem('mattress-quote-list', JSON.stringify(globalList));
    notifyListeners();
  };

  const updateQuantity = (mattressId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromList(mattressId);
      return;
    }
    
    const itemIndex = globalList.findIndex(item => item.id === mattressId);
    if (itemIndex !== -1) {
      globalList[itemIndex].quantity = quantity;
      localStorage.setItem('mattress-quote-list', JSON.stringify(globalList));
      notifyListeners();
    }
  };

  const clearList = () => {
    globalList = [];
    localStorage.setItem('mattress-quote-list', JSON.stringify(globalList));
    notifyListeners();
  };

  const isInList = (mattressId: string) => {
    return globalList.some(item => item.id === mattressId);
  };

  const getTotalItems = () => {
    return globalList.reduce((total, item) => total + item.quantity, 0);
  };

  return {
    list: globalList,
    listCount: globalList.length,
    totalItems: getTotalItems(),
    isOpen,
    setIsOpen,
    addToList,
    removeFromList,
    updateQuantity,
    clearList,
    isInList
  };
};
